import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import { CircleHelpIcon } from 'lucide-react';
import { Modal } from '@bcecloud/portal-ui';
import { cn } from '@/lib/utils';
import { helpVideosForPath, isDocVisible, type HelpDoc } from '@/help/content';
import { useFeatureLookup } from '@/lib/app-features';
import { usePermissionLookup } from '@/lib/use-permission-lookup';
import { markHelpVideoWatched, readMyIdentity } from '@/lib/events';

/** The help videos for the current screen that this person may see, most relevant first. */
export function usePageHelpVideos(): readonly HelpDoc[] {
  const { pathname } = useLocation();
  const isFeatureOn = useFeatureLookup();
  const hasPermission = usePermissionLookup();
  return helpVideosForPath(pathname).filter((doc) => isDocVisible(doc, isFeatureOn, hasPermission));
}

/**
 * Whether to nudge someone to watch: the screen's main video is one they have
 * never started. Their other videos don't count, so a screen they already
 * learned stays quiet.
 */
export function shouldNudge(videos: readonly HelpDoc[], watched: ReadonlySet<string>): boolean {
  const main = videos[0]?.video?.src;
  return main !== undefined && !watched.has(main);
}

/**
 * The question-mark button in the top bar, beside ideas and notifications.
 * Renders nothing on a screen no help video covers; otherwise opens
 * {@link HelpVideoPlayer}. Until the person starts the screen's main video,
 * a yellow dot invites them to watch it.
 */
export function PageHelpVideosButton(): React.ReactElement | null {
  const videos = usePageHelpVideos();
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const identity = useQuery({ queryKey: ['my-identity'], queryFn: ({ signal }) => readMyIdentity(signal), staleTime: 5 * 60_000 });
  // Started this visit: the dot goes as soon as the video plays, before the save returns.
  const [startedNow, setStartedNow] = useState<readonly string[]>([]);
  const watched = useMutation({
    mutationFn: (video: string) => markHelpVideoWatched(video),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['my-identity'] });
    },
  });
  if (videos.length === 0) return null;
  const seen = new Set([...(identity.data?.watchedHelpVideos ?? []), ...startedNow]);
  // No nudge until we know what they have watched, so it never flashes on and off.
  const nudge = identity.data !== undefined && shouldNudge(videos, seen);
  const base = videos.length === 1 ? 'Watch how-to video' : `Watch how-to videos (${videos.length})`;
  const label = nudge ? `${base}: new to you` : base;
  const onWatched = (video: string): void => {
    if (seen.has(video)) return;
    setStartedNow((current) => [...current, video]);
    watched.mutate(video);
  };
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={label}
        title={nudge ? 'New here? Watch how this screen works' : label}
        className={cn(
          'relative flex size-9 shrink-0 items-center justify-center rounded-portal-pill border transition-colors hover:border-brand hover:text-brand',
          nudge ? 'border-brand text-brand' : 'border-portal-line text-portal-muted',
        )}
      >
        <CircleHelpIcon aria-hidden className="size-4" />
        {nudge ? (
          <span aria-hidden className="absolute -top-0.5 -right-0.5 flex size-3">
            <span className="absolute inline-flex size-full rounded-full bg-bce-yellow opacity-75 motion-safe:animate-ping" />
            <span className="relative inline-flex size-3 rounded-full bg-bce-yellow ring-2 ring-white" />
          </span>
        ) : null}
      </button>
      {open ? <HelpVideoPlayer videos={videos} onClose={() => setOpen(false)} onWatched={onWatched} /> : null}
    </>
  );
}

export interface HelpVideoPlayerProps {
  /** Most relevant first; the first one plays on open. */
  readonly videos: readonly HelpDoc[];
  readonly onClose: () => void;
  /** Called with a video's file each time one starts playing. */
  readonly onWatched?: (video: string) => void;
}

/**
 * A popup player: the most relevant video plays first, each one rolls into
 * the next when it ends, and the rest sit under it as cards to pick from.
 */
export function HelpVideoPlayer({ videos, onClose, onWatched }: HelpVideoPlayerProps): React.ReactElement | null {
  const [current, setCurrent] = useState(0);
  const doc = videos[current];
  if (!doc?.video) return null;
  const hasNext = current + 1 < videos.length;
  const src = doc.video.src;
  return (
    // About 90% of the screen, and only the ✕ (or Escape) closes it, so a stray click never stops a video.
    <Modal
      open
      title={doc.title}
      subtitle={doc.summary}
      onClose={onClose}
      closeOnBackdrop={false}
      className="w-[90vw] max-w-none"
      phoneFullScreen
    >
      <figure className="flex justify-center overflow-hidden rounded-portal-card bg-black">
        {/* Captions are burned into the video, so there is no separate track. */}
        <video
          key={doc.video.src}
          className={cn(
            'block aspect-video max-w-full',
            // As big as 90% of the screen allows, leaving room for the title and the cards.
            videos.length > 1
              ? 'h-[min(calc((90vw-3rem)*0.5625),calc(90vh-17rem))]'
              : 'h-[min(calc((90vw-3rem)*0.5625),calc(90vh-9rem))]',
          )}
          src={doc.video.src}
          poster={doc.video.poster}
          controls
          autoPlay
          playsInline
          onPlay={() => onWatched?.(src)}
          onEnded={() => {
            if (hasNext) setCurrent(current + 1);
          }}
          aria-label={`${doc.title}: walkthrough video`}
        />
      </figure>
      <p className="mt-3">
        <Link to={doc.path} onClick={onClose} className="font-bold text-brand-link hover:underline">
          Read the {doc.title} guide
        </Link>
      </p>
      {videos.length > 1 ? (
        <ul aria-label="More how-to videos" className="mt-4 flex gap-3 overflow-x-auto pb-1">
          {videos.map((other, index) => (
            <li key={other.path} className="w-44 shrink-0">
              <button
                type="button"
                onClick={() => setCurrent(index)}
                aria-pressed={index === current}
                className={cn(
                  'w-full overflow-hidden rounded-portal-card border text-left transition-colors',
                  index === current ? 'border-brand ring-2 ring-brand' : 'border-portal-line hover:border-brand',
                )}
              >
                <img src={other.video?.poster} alt="" className="block aspect-video w-full bg-portal-line object-cover" />
                <span className="block p-2 text-portal-micro font-bold text-portal-ink">
                  {index === current ? 'Playing: ' : index === current + 1 ? 'Up next: ' : ''}
                  {other.title}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </Modal>
  );
}
